import React from 'react';
import { Components } from 'react-markdown';

/**
 * Clean, document-style markdown components (no cards, just clean paragraphs)
 */
export const cleanMarkdownComponents: Components = {
  // H2 - Major sections
  h2: ({ children, ...props }) => {
    const text = String(children);
    // Extract emoji if present
    const emojiMatch = text.match(/^([\u{1F300}-\u{1F9FF}])\s*(.+)/u);
    if (emojiMatch) {
      const [, emoji, title] = emojiMatch;
      return (
        <div className="mt-8 mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <span className="text-3xl">{emoji}</span>
            <span>{title}</span>
          </h2>
          <div className="h-1 w-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mt-2"></div>
        </div>
      );
    }
    return (
      <div className="mt-8 mb-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {children}
        </h2>
        <div className="h-1 w-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mt-2"></div>
      </div>
    );
  },

  // H3 - Subsections
  h3: ({ children, ...props }) => {
    return (
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mt-6 mb-3">
        {children}
      </h3>
    );
  },

  // H4 - Minor headings
  h4: ({ children, ...props }) => {
    return (
      <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mt-4 mb-2">
        {children}
      </h4>
    );
  },

  // Paragraphs - clean, readable
  p: ({ children, ...props }) => {
    const text = String(children);

    // Special styling for "Bottom Line" conclusions
    if (text.includes('→ **Bottom Line:**') || text.includes('Bottom Line:')) {
      return (
        <div className="my-4 pl-4 border-l-4 border-purple-500 bg-purple-50 dark:bg-purple-900/10 py-3 pr-4">
          <p className="text-base text-gray-900 dark:text-white font-medium leading-relaxed">
            {children}
          </p>
        </div>
      );
    }

    // Special styling for "WHAT THIS MEANS"
    if (text.includes('💡 WHAT THIS MEANS:') || text.includes('WHAT THIS MEANS:')) {
      return (
        <div className="my-4 pl-4 border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/10 py-3 pr-4">
          <p className="text-base text-gray-800 dark:text-gray-200 leading-relaxed">
            {children}
          </p>
        </div>
      );
    }

    // Warning/alert paragraphs
    if (text.startsWith('⚠️')) {
      return (
        <div className="my-4 pl-4 border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/10 py-3 pr-4">
          <p className="text-base text-gray-800 dark:text-gray-200 leading-relaxed">
            {children}
          </p>
        </div>
      );
    }

    // Regular paragraphs
    return (
      <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed my-3">
        {children}
      </p>
    );
  },

  // Strong/bold - keep simple
  strong: ({ children, ...props }) => {
    return (
      <strong className="font-semibold text-gray-900 dark:text-white">
        {children}
      </strong>
    );
  },

  // Tables - clean, professional
  table: ({ children, ...props }) => {
    return (
      <div className="my-6 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
          {children}
        </table>
      </div>
    );
  },

  thead: ({ children, ...props }) => {
    return (
      <thead className="bg-gray-50 dark:bg-gray-800">
        {children}
      </thead>
    );
  },

  tbody: ({ children, ...props }) => {
    return (
      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
        {children}
      </tbody>
    );
  },

  tr: ({ children, ...props }) => {
    return (
      <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
        {children}
      </tr>
    );
  },

  th: ({ children, ...props }) => {
    return (
      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
        {children}
      </th>
    );
  },

  td: ({ children, ...props }) => {
    return (
      <td className="px-6 py-4 text-sm text-gray-800 dark:text-gray-200">
        {children}
      </td>
    );
  },

  // Lists - clean bullets/numbers
  ul: ({ children, ...props }) => {
    return (
      <ul className="my-4 ml-6 space-y-2 list-disc marker:text-blue-500">
        {children}
      </ul>
    );
  },

  ol: ({ children, ...props }) => {
    return (
      <ol className="my-4 ml-6 space-y-2 list-decimal marker:text-blue-500 marker:font-semibold">
        {children}
      </ol>
    );
  },

  li: ({ children, ...props }) => {
    const text = String(children);

    // Checkbox items
    if (text.startsWith('[ ]') || text.startsWith('[x]') || text.startsWith('[X]')) {
      const checked = text.startsWith('[x]') || text.startsWith('[X]');
      const content = text.replace(/^\[[ xX]\]\s*/, '');
      return (
        <li className="flex items-start gap-2 ml-0 list-none">
          <span className="text-lg mt-0.5 flex-shrink-0">
            {checked ? '✅' : '☐'}
          </span>
          <span className="text-base text-gray-700 dark:text-gray-300">{content}</span>
        </li>
      );
    }

    // Regular list items
    return (
      <li className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
        {children}
      </li>
    );
  },

  // Horizontal rules
  hr: ({ ...props }) => {
    return (
      <hr className="my-8 border-t-2 border-gray-200 dark:border-gray-700" />
    );
  },

  // Blockquotes
  blockquote: ({ children, ...props }) => {
    return (
      <blockquote className="my-4 pl-4 border-l-4 border-gray-300 dark:border-gray-600 italic text-gray-700 dark:text-gray-300">
        {children}
      </blockquote>
    );
  },

  // Code
  code: ({ inline, className, children, ...props }: any) => {
    if (inline) {
      return (
        <code className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-sm rounded text-purple-600 dark:text-purple-400 font-mono">
          {children}
        </code>
      );
    }
    return (
      <pre className="my-4 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto border border-gray-200 dark:border-gray-700">
        <code className="text-sm font-mono text-gray-800 dark:text-gray-200">
          {children}
        </code>
      </pre>
    );
  },
};
